// =============================================================================
// Portal API Client Instance
// =============================================================================
// Single ApiClient instance for the portal app.
// Base URL comes from VITE_API_URL env var (set in .env or build-time).
//
// IMPORTANT: VITE_API_URL must be defined in .env (local dev) or as a
// build-time env var (CI/CD). There is no hardcoded fallback — missing
// this var will produce a clear error in the browser console.
// =============================================================================

import { createApiClient, SessionKeeper } from '@tkpod/api-client';

const baseUrl = import.meta.env.VITE_API_URL as string | undefined;

if (!baseUrl) {
  throw new Error(
    '[Portal] VITE_API_URL is not defined. ' +
      'Add VITE_API_URL=http://localhost:3000 to your .env file.',
  );
}

const TOKEN_TTL_MS = 8 * 60 * 60 * 1000; // Must match JWT_EXPIRES_IN

let pendingRefresh: Promise<boolean> | null = null;

function doRefresh(): Promise<boolean> {
  if (pendingRefresh) return pendingRefresh;

  pendingRefresh = (async () => {
    try {
      const res = await fetch(`${baseUrl}/api/v1/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      return res.ok;
    } catch {
      return false;
    }
  })().finally(() => {
    pendingRefresh = null;
  });

  return pendingRefresh;
}

async function handleSessionExpired(): Promise<void> {
  const publicPaths = [
    '/login',
    '/register',
    '/forgot-password',
    '/reset-password',
    '/verify-email',
  ];
  if (publicPaths.includes(window.location.pathname) || window.location.pathname.startsWith('/join')) {
    return;
  }
  const { useAuthStore } = await import('./auth-store');
  useAuthStore.setState({ status: 'unauthenticated', user: null, error: null });
  window.location.href = '/login';
}

let sessionKeeper: SessionKeeper | null = null;

export const api = createApiClient({
  baseUrl,
  credentials: 'include',
  timeout: 30_000,
  maxRetries: 2,

  onUnauthorized: async () => {
    const ok = await doRefresh();
    if (ok) return true;

    await handleSessionExpired();
    return false;
  },

  onTokenRefreshed: () => {
    sessionKeeper?.resetTimer();
  },

  onSessionExpired: () => {
    void handleSessionExpired();
  },
});

/**
 * Start the proactive session keeper.
 * Call once after successful login or session initialization.
 */
export function startSessionKeeper(): void {
  sessionKeeper?.dispose();
  sessionKeeper = new SessionKeeper({
    tokenTtlMs: TOKEN_TTL_MS,
    refreshFn: doRefresh,
    onSessionExpired: () => void handleSessionExpired(),
  });
}

/**
 * Stop the proactive session keeper (e.g. on logout).
 */
export function stopSessionKeeper(): void {
  sessionKeeper?.dispose();
  sessionKeeper = null;
}
